import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CheckCircle, XCircle, Settings, MessageCircle, CalendarCheck, CreditCard, ExternalLink, AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const serviceIcons = { whatsapp: MessageCircle, google_calendar: CalendarCheck, stripe: CreditCard };
const serviceNames = { whatsapp: 'WhatsApp (Twilio)', google_calendar: 'Google Calendar + Meet', stripe: 'Stripe Pagamentos' };

export const IntegrationSettings = () => {
  const [integrations, setIntegrations] = useState([]);
  const [googleAuthorized, setGoogleAuthorized] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Wipe DB dialog state
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipePassword, setWipePassword] = useState('');
  const [wipeConfirm, setWipeConfirm] = useState('');
  const [wipeLoading, setWipeLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const [intRes, gRes] = await Promise.all([
        axios.get(`${API_URL}/api/integrations/status`, { withCredentials: true }),
        axios.get(`${API_URL}/api/google/status`, { withCredentials: true }).catch(() => ({ data: { authorized: false } }))
      ]);
      setIntegrations(intRes.data.integrations || []);
      setGoogleAuthorized(gRes.data.authorized);
    } catch (error) { console.error('Error:', error); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleConnectGoogle = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/google/authorize`, { withCredentials: true });
      if (data.authorize_url) window.location.href = data.authorize_url;
    } catch (e) { console.error(e); }
    setConnecting(false);
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Desconectar Google Calendar? Você precisará autorizar novamente.')) return;
    try {
      await axios.delete(`${API_URL}/api/google/disconnect`, { withCredentials: true });
      toast.success('Google desconectado. Clique em "Conectar Google Calendar" para autorizar novamente.');
      setGoogleAuthorized(false);
      fetchStatus();
    } catch (e) {
      toast.error('Erro ao desconectar Google');
    }
  };

  const handleWipeDatabase = async (e) => {
    e.preventDefault();
    if (wipeConfirm.trim().toUpperCase() !== 'APAGAR') {
      toast.error('Digite APAGAR para confirmar');
      return;
    }
    setWipeLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/wipe-database`,
        { password: wipePassword, confirm: wipeConfirm },
        { withCredentials: true }
      );
      const summary = Object.entries(data.deleted || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
      toast.success(`Base limpa. ${summary}`);
      setWipeOpen(false);
      setWipePassword('');
      setWipeConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao apagar base');
    }
    setWipeLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="integration-settings">
      <div>
        <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Configuracoes</h2>
        <p className="text-[#5F6B85]">Status das integracoes externas</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {integrations.map((integration) => {
          const Icon = serviceIcons[integration.service] || Settings;
          const name = serviceNames[integration.service] || integration.service;
          const isGoogle = integration.service === 'google_calendar';
          const isActive = isGoogle ? (integration.enabled && googleAuthorized) : integration.enabled;

          return (
            <Card key={integration.service} className={`border ${isActive ? 'border-[#0F1B3D]' : 'border-[#E5DCC8]'}`} data-testid={`integration-${integration.service}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isActive ? 'bg-[#0F1B3D]/10' : 'bg-[#7A85A0]/10'}`}>
                      <Icon className={`h-5 w-5 ${isActive ? 'text-[#0F1B3D]' : 'text-[#7A85A0]'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{name}</CardTitle>
                      <CardDescription className="text-xs">Provedor: {integration.provider}</CardDescription>
                    </div>
                  </div>
                  {isActive ? (
                    <div className="flex items-center gap-1 text-[#0F1B3D]"><CheckCircle className="h-5 w-5" /><span className="text-xs font-medium">Ativo</span></div>
                  ) : (
                    <div className="flex items-center gap-1 text-[#7A85A0]"><XCircle className="h-5 w-5" /><span className="text-xs font-medium">{isGoogle && integration.enabled ? 'Aguardando' : 'Inativo'}</span></div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {isGoogle && integration.enabled && !googleAuthorized && (
                  <Button onClick={handleConnectGoogle} disabled={connecting} className="w-full bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="connect-google-btn">
                    <ExternalLink className="h-4 w-4 mr-2" />{connecting ? 'Conectando...' : 'Conectar Google Calendar'}
                  </Button>
                )}
                {isGoogle && googleAuthorized && (
                  <div className="space-y-2">
                    <p className="text-xs text-[#0F1B3D] font-medium">Google Calendar conectado. Meet sera gerado automaticamente nas consultas.</p>
                    <Button variant="outline" size="sm" onClick={handleDisconnectGoogle} className="w-full text-[#A8174E] border-[#A8174E]" data-testid="disconnect-google-btn">
                      Reconectar / Desconectar
                    </Button>
                  </div>
                )}
                {!integration.enabled && integration.config_needed?.length > 0 && (
                  <div className="bg-[#FAF6EE] rounded-lg p-3 mt-2">
                    <p className="text-xs font-medium text-[#5F6B85] mb-2">Para ativar, adicione no .env:</p>
                    <div className="space-y-1">
                      {integration.config_needed.map((config, i) => (
                        <code key={`cfg-${integration.service}-${i}`} className="block text-xs text-[#0F1B3D] bg-white px-2 py-1 rounded">{config}</code>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Danger Zone */}
      <Card className="border-2 border-[#A8174E]/40 bg-[#A8174E]/5 mt-8" data-testid="danger-zone">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#A8174E]/15">
              <AlertTriangle className="h-5 w-5 text-[#A8174E]" />
            </div>
            <div>
              <CardTitle className="text-base text-[#A8174E]">Zona de Perigo</CardTitle>
              <CardDescription className="text-xs">Ações irreversíveis sobre a base de dados</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-[#0F1B3D]">Limpar base de dados</p>
              <p className="text-xs text-[#5F6B85] mt-1">
                Apaga todos os pacientes, consultas, evoluções, prontuários, pagamentos e notificações.
                Mantém: seu usuário admin, disponibilidade da agenda e tabela de preços.
              </p>
            </div>
            <Button
              variant="outline"
              className="border-[#A8174E] text-[#A8174E] hover:bg-[#A8174E] hover:text-white"
              onClick={() => setWipeOpen(true)}
              data-testid="open-wipe-dialog-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Limpar Base
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={wipeOpen} onOpenChange={(open) => { if (!open) { setWipeOpen(false); setWipePassword(''); setWipeConfirm(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#A8174E] flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Limpar base de dados
            </DialogTitle>
            <DialogDescription>
              Esta ação <strong>não pode ser desfeita</strong>. Todos os pacientes, consultas,
              evoluções, prontuários, pagamentos e notificações serão apagados.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleWipeDatabase} className="space-y-4">
            <div className="space-y-2">
              <Label>Confirme sua senha de admin</Label>
              <Input
                type="password"
                value={wipePassword}
                onChange={(e) => setWipePassword(e.target.value)}
                required
                data-testid="wipe-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Digite <code className="text-[#A8174E] font-bold">APAGAR</code> para confirmar</Label>
              <Input
                type="text"
                value={wipeConfirm}
                onChange={(e) => setWipeConfirm(e.target.value)}
                placeholder="APAGAR"
                required
                data-testid="wipe-confirm-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setWipeOpen(false)} data-testid="wipe-cancel-btn">
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#A8174E] hover:bg-[#8E1142] text-white"
                disabled={wipeLoading || wipeConfirm.trim().toUpperCase() !== 'APAGAR'}
                data-testid="wipe-submit-btn"
              >
                {wipeLoading ? 'Apagando...' : 'Apagar Base'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
